import { useEffect, useRef, type RefObject } from 'react';
import type { ModelViewerElement } from '@google/model-viewer';

export type SavedModelViewerCamera = {
  orbit: string;
  target: string;
  fov: string;
};

type CameraBinding = {
  attribute: 'camera-orbit' | 'camera-target' | 'field-of-view';
  property: 'cameraOrbit' | 'cameraTarget' | 'fieldOfView';
};

const SAVED_CAMERA_BINDINGS: Record<keyof SavedModelViewerCamera, CameraBinding> = {
  orbit: { attribute: 'camera-orbit', property: 'cameraOrbit' },
  target: { attribute: 'camera-target', property: 'cameraTarget' },
  fov: { attribute: 'field-of-view', property: 'fieldOfView' },
};

function applySavedCameraValue(
  mv: ModelViewerElement,
  key: keyof SavedModelViewerCamera,
  value: string
): void {
  const { attribute, property } = SAVED_CAMERA_BINDINGS[key];
  mv.setAttribute(attribute, value);
  mv[property] = value;
}

/**
 * Applies saved orbit / target / FOV once per model URL on load.
 *
 * Prevents a visible zoom jump when preview capture updates camera attrs without
 * remounting the `<model-viewer>` element (create & edit product flows).
 */
export function useSavedModelViewerCamera(
  modelViewerRef: RefObject<ModelViewerElement>,
  modelUrl: string,
  savedCamera: SavedModelViewerCamera
) {
  const savedCameraRef = useRef(savedCamera);
  const appliedCameraForUrlRef = useRef('');

  useEffect(() => {
    savedCameraRef.current = savedCamera;
  }, [savedCamera.orbit, savedCamera.target, savedCamera.fov]);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !modelUrl) {
      appliedCameraForUrlRef.current = '';
      return;
    }

    const applySavedCameraOnce = () => {
      if (appliedCameraForUrlRef.current === modelUrl) return;
      appliedCameraForUrlRef.current = modelUrl;

      const { orbit, target, fov } = savedCameraRef.current;
      if (orbit) applySavedCameraValue(mv, 'orbit', orbit);
      if (target) applySavedCameraValue(mv, 'target', target);
      if (fov) applySavedCameraValue(mv, 'fov', fov);
    };

    const onLoad = () => applySavedCameraOnce();
    mv.addEventListener('load', onLoad);

    try {
      if (mv.loaded) {
        applySavedCameraOnce();
      }
    } catch {
      // Wait for load event.
    }

    return () => {
      mv.removeEventListener('load', onLoad);
    };
  }, [modelUrl, modelViewerRef]);
}
